import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

type MovieCardProps = {
  movie: Movie;
  variant?: 'grid' | 'list';
  isFavorite?: boolean;
  onToggleFavorite?: (movie: Movie) => void;
  index?: number;
};

export default function MovieCard({
  movie,
  variant = 'grid',
  isFavorite = false,
  onToggleFavorite,
  index = 0,
}: MovieCardProps) {
  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.(movie);
  };

  if (variant === 'list') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100 sm:gap-4 sm:p-4"
        style={{ animationDelay: `${index * 40}ms` }}
      >
        <div className="h-32 w-[88px] shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-40 sm:w-28">
          <img
            src={movie.poster}
            alt={`${movie.title} 电影海报`}
            title={`${movie.title} - 北条麻妃电影`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-700 sm:text-lg">
              {movie.title}
            </h3>
            <button
              type="button"
              onClick={handleFavorite}
              aria-label={isFavorite ? '取消收藏' : '收藏'}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                isFavorite
                  ? 'border-accent-300 bg-accent-100 text-accent-700'
                  : 'border-background-300 text-foreground-500 hover:text-accent-600'
              }`}
            >
              <i className={isFavorite ? 'ri-heart-3-fill text-sm' : 'ri-heart-3-line text-sm'}></i>
            </button>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {movie.year}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-star-line"></i>
              {movie.director}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {movie.runtime} 分钟
            </span>
            <span className="flex items-center gap-1 text-accent-600 font-medium">
              <i className="ri-star-fill"></i>
              {movie.rating.toFixed(1)}
            </span>
          </div>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {movie.synopsis}
          </p>
          <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
            {movie.genres.map((g) => (
              <span
                key={g}
                className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group animate-fade-up flex flex-col"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={movie.poster}
          alt={`${movie.title} 电影海报`}
          title={`${movie.title} - 北条麻妃电影`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
        <span className="absolute left-2 top-2 rounded-full bg-foreground-950/60 px-2 py-0.5 text-xs font-medium text-background-50 backdrop-blur-sm">
          {movie.year}
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label={isFavorite ? '取消收藏' : '收藏'}
          className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
            isFavorite
              ? 'bg-accent-500 text-background-50'
              : 'bg-foreground-950/40 text-background-50 hover:bg-accent-500'
          }`}
        >
          <i className={isFavorite ? 'ri-heart-3-fill text-sm' : 'ri-heart-3-line text-sm'}></i>
        </button>
        <span className="absolute bottom-2 left-2 flex items-center gap-1 text-xs font-medium text-background-50">
          <i className="ri-star-fill text-accent-300"></i>
          {movie.rating.toFixed(1)}
        </span>
      </div>
      <h3 className="mt-3 line-clamp-1 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
        {movie.title}
      </h3>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {movie.genres.slice(0, 2).map((g) => (
          <span
            key={g}
            className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900"
          >
            {g}
          </span>
        ))}
      </div>
    </Link>
  );
}